import { Box, Button, Container, FormField, Header, Icon, Modal, SegmentedControl, SpaceBetween, Toggle } from '@amzn/awsui-components-core';
import { ColDef, GetDataPath, ValueGetterParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingSpinner } from 'src/components/generic-components/LoadingSpinner';
import { useFinTechContext } from 'src/components/context/FintechContextProvider';
import { useTheme } from 'src/components/context/ThemeContextProvider';
import { ErrorContainer, filtersLabel } from '../FinTechOpsCommonElements';
import { useUserMetricsContext } from './UserMetricsContext';
import { HierarchyRow } from './UserMetricsInterfaces';
import {
  buildManagerHierarchyData,
  buildApplicationFirstHierarchyData,
  filterHierarchyByLevel,
  getUniqueManagerLevels,
  filterApplicationHierarchyByLevel,
  filterAndAggregateUserDetails,
  buildHierarchyColumnDefs
} from './UserMetricsDataProcessing';
import {
  HIERARCHY_VIEW_MODE,
  HierarchyViewMode,
  DEFAULT_MANAGER_LEVEL,
  HIERARCHY_GRID_STYLE,
  HIERARCHY_DEFAULT_COL_DEF,
  HIERARCHY_LOCALE_TEXT,
  processExcelCellValue
} from './UserMetricsConstants';
import { UserMetricsUserDetailsTable } from './UserMetricsUserDetailsTable';
import styles from './styles.module.scss';

// ===== Cell Renderers =====

/**
 * Custom cell renderer for the "View Users" button in manager rows.
 * Only renders for manager-type rows, not application rows.
 * Supports dark mode via context.isDarkMode
 */
const DetailToggleCellRenderer = (props: any) => {
  const { data, context, value } = props;
  const isDarkMode = context?.isDarkMode ?? false;

  // Get display name from valueGetter result
  const displayName = value || '';

  // Only show button for manager rows
  const showButton = data?.type === 'manager';

  // Select button class based on theme
  const buttonClassName = isDarkMode ? styles.viewUsersButtonDark : styles.viewUsersButtonLight;

  return (
    <span className={styles.cellRendererContainer}>
      <span>{displayName}</span>
      {showButton && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            context?.onViewUsersClick?.(data.manager_name, data);
          }}
          className={buttonClassName}
        >
          View Users
        </button>
      )}
    </span>
  );
};

// ===== Component Props =====
interface UserMetricsHierarchyMetricsTabProps {
  hierarchyViewMode: HierarchyViewMode;
}

export const UserMetricsHierarchyMetricsTab: React.FC<UserMetricsHierarchyMetricsTabProps> = ({ hierarchyViewMode }) => {
  // ===== Context =====
  const userMetricsContext = useUserMetricsContext();
  const finOpsContext = useFinTechContext();
  const { agGridTheme, isDarkMode } = useTheme();

  // ===== Grid References =====
  const hierarchyGridRef = useRef<AgGridReact>(null);

  // ===== Get shared state from context (persistent across tabs) =====
  const { isSubApplicationView, setIsSubApplicationView, showViewsColumns, setShowViewsColumns, selectedLevel, setSelectedLevel } =
    userMetricsContext;

  // ===== Hierarchy Data State =====
  const [hierarchyData, setHierarchyData] = useState<HierarchyRow[]>([]);
  const [applicationHierarchyData, setApplicationHierarchyData] = useState<HierarchyRow[]>([]);
  const [availableLevels, setAvailableLevels] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);

  // ===== Detail Panel State =====
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [selectedManagerName, setSelectedManagerName] = useState<string | null>(null);
  const [selectedSubApplication, setSelectedSubApplication] = useState<string | null>(null);

  // ===== User Details Grid Reference (for modal download) =====
  const userDetailsGridRef = useRef<AgGridReact | null>(null);

  // ===== Data Processing Effects =====

  // Build hierarchy data from filtered context data
  useEffect(() => {
    if (userMetricsContext.filteredUserMetricsData && userMetricsContext.filteredUserMetricsData.length > 0) {
      // useApplicationType: true = aggregate by application_type, false = by application (sub-app)
      // isSubApplicationView: false = Application (default), true = Sub-Application
      const useApplicationType = !isSubApplicationView;

      // Build manager-first hierarchy with manager roster data for team size visibility
      // Manager roster data contains managers with no user metrics but valid team sizes
      const managerData = buildManagerHierarchyData(
        userMetricsContext.filteredUserMetricsData,
        undefined,
        useApplicationType,
        userMetricsContext.managerRosterMetricsData
      );
      setHierarchyData(managerData);

      // Build application-first hierarchy (roster managers not included since they have no app data)
      const appData = buildApplicationFirstHierarchyData(userMetricsContext.filteredUserMetricsData, undefined, useApplicationType);
      setApplicationHierarchyData(appData);

      const levels = getUniqueManagerLevels(managerData);
      setAvailableLevels(levels);

      // Extract months from the data (look at first manager row's dynamic keys)
      // Exclude "total" since total_views is a separate total column, not a month
      const months: string[] = [];
      if (managerData.length > 0) {
        const firstRow = managerData[0];
        Object.keys(firstRow).forEach((key) => {
          if (key.endsWith('_views')) {
            const month = key.replace('_views', '');
            // Skip "total" - it's not a month, it's the total column
            if (month !== 'total' && !months.includes(month)) {
              months.push(month);
            }
          }
        });
      }
      setAvailableMonths(months);
    } else {
      // Clear hierarchy data when no data matches filters
      setHierarchyData([]);
      setApplicationHierarchyData([]);
      setAvailableLevels([]);
      setAvailableMonths([]);
    }
  }, [userMetricsContext.filteredUserMetricsData, userMetricsContext.managerRosterMetricsData, isSubApplicationView]);

  // ===== Filtered/Computed Data =====

  /**
   * Applies level filter to hierarchy data.
   * Shows selected level and all levels below (subordinates).
   */
  const displayedHierarchyData = useMemo(() => {
    const sourceData = hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER ? hierarchyData : applicationHierarchyData;

    if (hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER) {
      return filterHierarchyByLevel(sourceData, selectedLevel);
    } else {
      return filterApplicationHierarchyByLevel(sourceData, selectedLevel);
    }
  }, [hierarchyData, applicationHierarchyData, selectedLevel, hierarchyViewMode]);

  // Level filter options - sorted high to low (L11 first)
  const levelOptions = useMemo(() => {
    if (availableLevels.length === 0)
      return [10].map((level) => ({
        text: `L${level}`,
        id: level.toString()
      }));
    return availableLevels.map((level) => ({
      text: `L${level}`,
      id: level.toString()
    }));
  }, [availableLevels]);

  // AG-Grid tree data path function
  const getDataPath: GetDataPath = useCallback((data: HierarchyRow) => {
    return data.path;
  }, []);

  // ===== Column Definitions =====

  // Dynamic column definitions for hierarchy grid - generated by utility function
  const columnDefsHierarchy = useMemo(
    () => buildHierarchyColumnDefs(availableMonths, hierarchyViewMode, DetailToggleCellRenderer, showViewsColumns),
    [availableMonths, hierarchyViewMode, showViewsColumns]
  );

  // Filtered user details using extracted utility function
  const filteredUserDetails = useMemo(() => {
    const isManagerView = hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER;
    // shouldAggregate is INVERTED from isSubApplicationView:
    // - Application mode (isSubApplicationView=false) → shouldAggregate=true → aggregated (fewer rows)
    // - Sub-Application mode (isSubApplicationView=true) → shouldAggregate=false → raw data (more rows)
    return filterAndAggregateUserDetails(
      userMetricsContext.filteredUserMetricsData,
      selectedManagerName,
      selectedSubApplication,
      isManagerView,
      !isSubApplicationView
    );
  }, [selectedManagerName, selectedSubApplication, userMetricsContext.filteredUserMetricsData, hierarchyViewMode, isSubApplicationView]);

  // Auto group column definition for tree data
  const autoGroupColumnDefHierarchy = useMemo<ColDef>(() => {
    // Determine header name based on view mode and sub-application toggle
    let headerName: string;
    if (hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER) {
      headerName = isSubApplicationView ? 'Manager / Sub-Application' : 'Manager / Application';
    } else {
      headerName = isSubApplicationView ? 'Sub-Application / Manager' : 'Application / Manager';
    }

    return {
      headerName,
      minWidth: 300,
      flex: 2,
      sort: 'desc',
      cellRendererParams: {
        suppressCount: true,
        checkbox: false,
        innerRenderer: DetailToggleCellRenderer
      },
      valueGetter: (params: ValueGetterParams) => {
        if (!params.data) return '';

        if (params.data.type === 'application') {
          return params.data.application || '';
        }
        return params.data.manager_name || '';
      },
      // Custom comparator: Applications first (sorted by metric), then Managers (sorted by metric)
      comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
        const typeA = nodeA?.data?.type || '';
        const typeB = nodeB?.data?.type || '';

        // Different types: applications come before managers
        // Since sort: 'desc' is applied, we return positive to put apps first
        if (typeA !== typeB) {
          // 'application' should come before 'manager'
          // With desc sort, returning positive means A comes first
          return typeA === 'application' ? 1 : -1;
        }

        // Same type: sort by metric (highest first for desc sort)
        // Application rows use 'views' field, Manager rows use 'total_views' field
        const viewsA = nodeA?.data?.total_views ?? nodeA?.data?.views ?? 0;
        const viewsB = nodeB?.data?.total_views ?? nodeB?.data?.views ?? 0;
        const metricA = showViewsColumns ? viewsA : nodeA?.data?.unique_users || 0;
        const metricB = showViewsColumns ? viewsB : nodeB?.data?.unique_users || 0;

        // Return difference for numeric sort (desc order with sort: 'desc')
        return metricA - metricB;
      }
    };
  }, [hierarchyViewMode, isSubApplicationView, showViewsColumns]);

  // ===== Export Handlers =====

  /**
   * Custom flat export that bypasses AG-Grid's tree data export to avoid Excel XML format errors.
   * Exports all rows (managers + applications) as flat CSV data that opens cleanly in Excel.
   */
  const onDownloadClickHierarchy = useCallback(() => {
    if (!displayedHierarchyData || displayedHierarchyData.length === 0) return;

    // Build header row - order: Hierarchy Path, Manager Name, Level, then monthly columns (Users only), Total Users
    const headers = ['Hierarchy Path', 'Manager Name', 'Level', ...availableMonths.map((month) => `${month} Users`), 'Total Users'];

    // Build data rows
    const rows = displayedHierarchyData.map((row) => {
      const hierarchyPath = row.path?.join(' > ') || '';
      const managerName = row.type === 'manager' ? row.manager_name || '' : '';
      const level = row.type === 'manager' && row.manager_level ? `L${row.manager_level}` : '';

      // Monthly user counts
      const monthlyValues = availableMonths.map((month) => {
        const value = row[`${month}_users`];
        return value !== undefined && value !== null ? value : 0;
      });

      // Total unique users
      const totalUsers = row.unique_users !== undefined ? row.unique_users : 0;

      return [hierarchyPath, managerName, level, ...monthlyValues, totalUsers];
    });

    // Convert to CSV string
    const escapeCSV = (value: any): string => {
      const str = String(value ?? '');
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [headers.map(escapeCSV).join(','), ...rows.map((row) => row.map(escapeCSV).join(','))].join('\n');

    // Create and download file
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel UTF-8
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER
        ? `Manager_Hierarchy_Metrics_${format(new Date(), 'yyyy-MM-dd')}.csv`
        : `Application_Hierarchy_Metrics_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [displayedHierarchyData, availableMonths, hierarchyViewMode]);

  // ===== Event Handlers =====

  // Handle level filter change
  const handleLevelChange = useCallback((event: { detail: { selectedId: string } }) => {
    const selectedValue = event.detail.selectedId;
    if (selectedValue) {
      setSelectedLevel(parseInt(selectedValue));
    }
  }, []);

  // Handle grid ref from UserMetricsUserDetailsTable
  const handleUserDetailsGridReady = useCallback((gridRef: React.RefObject<AgGridReact>) => {
    userDetailsGridRef.current = gridRef.current;
  }, []);

  // Handle download for user details modal
  const onDownloadClickUserDetails = useCallback(() => {
    if (userDetailsGridRef.current && userDetailsGridRef.current.api) {
      const params = {
        fileName: `User_Details_${selectedManagerName?.replace(/\s+/g, '_') || 'Unknown'}_${format(new Date(), 'yyyy-MM-dd')}`,
        suppressQuotes: true
      };
      userDetailsGridRef.current.api.exportDataAsExcel(params);
    }
  }, [selectedManagerName]);

  // Generate modal header title
  const modalHeaderTitle = useMemo(() => {
    if (hierarchyViewMode === HIERARCHY_VIEW_MODE.APPLICATION && selectedSubApplication) {
      return `User Details for ${selectedManagerName} - ${selectedSubApplication}`;
    }
    return `User Details for ${selectedManagerName}`;
  }, [selectedManagerName, selectedSubApplication, hierarchyViewMode]);

  // ===== Loading & Error States =====

  // Loading state
  if (finOpsContext.finTechOpsUserMetricsDataStatus === 'loading' || userMetricsContext.detailsStatus === 'loading') {
    return <LoadingSpinner />;
  }

  // Error state
  if (finOpsContext.finTechOpsUserMetricsDataStatus === 'error' || userMetricsContext.detailsStatus === 'error') {
    return <ErrorContainer />;
  }

  // ===== Render =====

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="s">
                <Button iconName="download" onClick={onDownloadClickHierarchy}>
                  Download
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="horizontal" size="m" alignItems="center">
              {hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER ? 'By Manager' : 'By Application'}
              <div style={{ minWidth: '135px' }}>
                <Toggle onChange={({ detail }) => setIsSubApplicationView(detail.checked)} checked={isSubApplicationView}>
                  {isSubApplicationView ? 'Sub-Application' : 'Application'}
                </Toggle>
              </div>
              <div style={{ minWidth: '130px' }}>
                <Toggle onChange={({ detail }) => setShowViewsColumns(detail.checked)} checked={showViewsColumns}>
                  {showViewsColumns ? 'Views' : 'Unique Users'}
                </Toggle>
              </div>
              <Box>{filtersLabel('Manager Level')}</Box>
              <SegmentedControl selectedId={selectedLevel.toString()} onChange={handleLevelChange} options={levelOptions} />
            </SpaceBetween>
          </Header>
        }
      >
        <div style={HIERARCHY_GRID_STYLE} className={agGridTheme}>
          <AgGridReact
            key={`${hierarchyViewMode === HIERARCHY_VIEW_MODE.MANAGER ? 'manager' : 'application'}-${isSubApplicationView ? 'subapp' : 'app'}-${
              showViewsColumns ? 'views' : 'users'
            }-${isDarkMode ? 'dark' : 'light'}`}
            ref={hierarchyGridRef}
            rowData={displayedHierarchyData}
            columnDefs={columnDefsHierarchy}
            defaultColDef={HIERARCHY_DEFAULT_COL_DEF}
            autoGroupColumnDef={autoGroupColumnDefHierarchy}
            treeData={true}
            getDataPath={getDataPath}
            animateRows={true}
            groupDefaultExpanded={1}
            enableRangeSelection={true}
            suppressRowClickSelection={true}
            localeText={HIERARCHY_LOCALE_TEXT}
            context={{
              isDarkMode,
              onViewUsersClick: (managerName: string, data: any) => {
                setSelectedManagerName(managerName);
                // In Application View, extract sub-application from path[0]
                if (hierarchyViewMode === HIERARCHY_VIEW_MODE.APPLICATION && data?.path?.length > 0) {
                  setSelectedSubApplication(data.path[0]);
                } else {
                  setSelectedSubApplication(null);
                }
                setDetailPanelOpen(true);
              }
            }}
          />
        </div>
      </Container>
      {detailPanelOpen && selectedManagerName && (
        <Modal
          visible={detailPanelOpen}
          className="hideModalHeader"
          onDismiss={() => {
            setDetailPanelOpen(false);
            setSelectedManagerName(null);
          }}
          size="max"
        >
          <div className={styles.modalContentWrapper}>
            <SpaceBetween direction="vertical" size="s">
              <Header
                actions={
                  <SpaceBetween direction="horizontal" size="s">
                    <Button iconName="download" onClick={onDownloadClickUserDetails}>
                      Download
                    </Button>
                    <Button iconName="close" variant="icon" onClick={() => setDetailPanelOpen(false)}></Button>
                  </SpaceBetween>
                }
              >
                {modalHeaderTitle}
              </Header>
              <UserMetricsUserDetailsTable
                rowData={filteredUserDetails}
                selectedManagerName={selectedManagerName}
                selectedApplicationType={selectedSubApplication}
                hierarchyViewMode={hierarchyViewMode}
                isApplicationViewMode={!isSubApplicationView}
                onToggleViewMode={setIsSubApplicationView}
                onClose={() => {
                  setDetailPanelOpen(false);
                  setSelectedManagerName(null);
                }}
                hideContainer={true}
                onGridReady={handleUserDetailsGridReady}
              />
            </SpaceBetween>
          </div>
        </Modal>
      )}
    </SpaceBetween>
  );
};
